import { safelyEncodeBase64Url } from '@dev/ee-tools';

import { useTranslation } from 'react-i18next';
import { Journey, JourneyStatus, JourneySummary } from '@dev/ee-api/ee-api-models';
import { useDispatch } from 'react-redux';
import { push } from 'redux-first-history';
import { useMobile } from '@/services/hooks/useMobile';
import { AutomationPaths } from '@/services/dictionaries/Paths';
import { DeleteAutomation, UpdateAutomationStatusConfirmation } from '@/store/automations/actions';
import { UpdateAutomationStatusParams, DropdownOptions } from '@/store/automations/types';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';

export const useDropdownActions = () => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();
    const updateAutomationStatus = (params: UpdateAutomationStatusParams): void => {
        dispatch(UpdateAutomationStatusConfirmation(params));
    };
    const viewWorkflow = (params: string): void => {
        dispatch(push(params));
    };

    const handleDeleteAutomation = (name: string) => {
        const dialogParams: ShowDialogParams = {
            title: t('delete_automation', { count: 1 }),
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                dispatch(DeleteAutomation([name]));
            },
            cancelLabel: t('cancel'),
            body: t('deleteautomationsmessage'),
        };

        dispatch(ShowDialog(dialogParams));
    };

    const commonOptions = {
        [DropdownOptions.Pause]: (automation: JourneySummary) =>
            updateAutomationStatus({ name: automation.Name, status: JourneyStatus.Paused }),
        [DropdownOptions.Reactivate]: (automation: JourneySummary) =>
            updateAutomationStatus({ name: automation.Name, status: JourneyStatus.Active }),
        [DropdownOptions.Delete]: (automation: JourneySummary) => handleDeleteAutomation(automation.Name),
    };

    const desktopActions = {
        [DropdownOptions.ViewWorkflow]: (automation: JourneySummary) => {
            automation.Status === JourneyStatus.Active
                ? viewWorkflow(
                      AutomationPaths.AutomationPreview.replace(':name', safelyEncodeBase64Url(automation.Name))
                  )
                : viewWorkflow(
                      AutomationPaths.AutomationEditor.replace(':name', safelyEncodeBase64Url(automation.Name))
                  );
        },
        [DropdownOptions.Edit]: (automation: JourneySummary) =>
            dispatch(push(AutomationPaths.AutomationEditor.replace(':name', safelyEncodeBase64Url(automation.Name)))),
        [DropdownOptions.PauseAndEdit]: (automation: JourneySummary) =>
            updateAutomationStatus({
                name: automation.Name,
                status: JourneyStatus.Paused,
                isEditMode: true,
            }),
    };

    const mobileActionHandler = (
        option: { label?: string; value: DropdownOptions },
        automation: JourneySummary
    ): void => commonOptions[option.value]?.(automation);

    const desktopActionHandler = (option: { label?: string; value: DropdownOptions }, automation?: Journey): void =>
        ({ ...commonOptions, ...desktopActions }?.[option.value](automation));

    return isMobile ? mobileActionHandler : desktopActionHandler;
};
